import { createSelector } from 'reselect';
import _sortBy from 'lodash/sortBy';
import _get from 'lodash/get';
import { isAddingTocart, isAddingTocartMembership } from './loaderSelector';
import { getOosStatus } from './cartSelector';
import {
  getNewSizeRevampFlag,
  getDisableGSTContentFlag,
  getDefaultGSTMessage,
  getHurryMsgInvCap,
  getPriceRevealV2MobileFeatureFlag,
  getPriceRevealMobileData,
  getPriceRevealLUXEFlag,
  getShoppingAssistantFlag,
  getShoppingAssistantData,
  getSimilarProductsMaxLimit,
  getUrgencyDriverV2PDPEnable,
  getEmiDiscountEnable,
  getB2cSellerFlag
  // getPriceRevealMobileFeatureFlag
} from './featureFlagsSelector';
import { getPincodeFromCookie } from '../../edd/utils/utils';
import {
  getSlaOptionsForSku,
  getIsSlaFetching as getIsSlaFetchingForSku
} from '../molecules/DeliverySLA/selectSlaForSku';
import { getAddToCartState, getAddToCartStateForMembership } from './popupSelector';
import { getErrorMessage } from './errorSelector';
import {
  getProductProps,
  displayValue,
  calculateDiscount,
  extractVariantsFromProduct,
  extractSelectionValues,
  resolveB2cMandatoryInfo,
  resolveB2cSellerName,
  getSizeName,
  getStandardSize,
  getBrandDetails
} from '../utils/productUtils';
import { getTagValueFromTagEngine, getWishlistCountFromTagEngine, getPriceLableFromTagEngine, getPDPTagV2FromPosition, getSellingFastLableFromTagEngine } from '../../utils/product';
import {
  IsDealStart,
  IsDealEnd
} from '../../components/common/DodCommonComponent/DodComponentFunctionality';
import { allUndefined } from '../utils/objectUtils';
import { PRE_BUZZ, DURING_SALE } from '../constants/product.constants';

const getMetaData = (
  state: import('../types/rootState').RootState
): import('../types/metadata').MetaData | undefined => state.metaData;

const getSis = createSelector(getMetaData, metaData => (metaData?.sis ? metaData.name : false));

const getProduct = (
  state: import('../types/rootState').RootState
): import('../types/product-page-types').ProductState => state.product;

const getRecentlyViewedProducts = (state: import('../types/rootState').RootState): string =>
  state?.recentlyViewed?.recent?.join();

/** scopedView option when size + pincode loaded; null → use aggregator variant path. */
const getSelectedSlaOption = (state: any) => state?.edd?.selectedSlaOption || null;

/** True while scopedView (DELIVERY_DETAILS_V2) is in flight. */
const getIsSlaFetching = (state: any) => Boolean(state?.edd?.isSlaFetching);

/**
 * B2C with no scopedView answer for the size on screen — the catalogue has to
 * speak for the product instead.
 *
 * Keyed off the *response*, not off the pincode. A pincode alone proves nothing:
 * the edd reducer seeds `activePincode` from the PC cookie at store creation
 * (see its initial state), so a cookie left over from an earlier session makes
 * it look like a location is set when no call has ever been made for this sku.
 *
 * True in every case the card comes up empty — no pincode, no size, or a call
 * that returned nothing — and false only while one is in flight or once it has
 * landed with options, where the card owns the seller row and can name a
 * different one than the catalogue.
 */
const getIsB2cNoLocation = (state: any): boolean => {
  if (!getB2cSellerFlag(state)) return false;
  const pincode = state?.edd?.activePincode || getPincodeFromCookie();
  const skuId = state?.product?.selectedSizeCode || '';
  // Neither key the SLA slice is indexed by => no call is possible.
  if (!pincode || !skuId) return true;
  // Mid-flight: leave it to the card rather than flashing the catalogue seller.
  if (getIsSlaFetchingForSku(state, pincode, skuId)) return false;
  return getSlaOptionsForSku(state, pincode, skuId).length === 0;
};

/** True when the last scopedView call failed (network, 5xx, or statusCode 1). */
const getSlaFetchFailed = (state: any) => Boolean(state?.edd?.slaFetchFailed);

/**
 * Format scopedView price for PDP. Prefer displayformattedValue (same source as
 * SLA cards) — API `value` can lag / mismatch the display string.
 */
const formatSlaOptionPrice = (priceData?: {
  value?: number;
  displayformattedValue?: string;
  formattedValue?: string;
}): string => {
  if (!priceData) return '';
  const formatted = priceData.displayformattedValue || priceData.formattedValue;
  if (formatted) {
    const numeric = parseInt(String(formatted).replace(/\D/g, ''), 10);
    if (Number.isFinite(numeric)) return displayValue(numeric);
  }
  if (priceData.value != null && priceData.value !== '') {
    return displayValue(priceData.value);
  }
  return '';
};

const getPriceDetails = createSelector(
  getProduct,
  getSelectedSlaOption,
  getIsSlaFetching,
  (
    { discountPrice, discountPercentage, originalPrice, productDetails = {} },
    selectedSlaOption,
    isSlaFetching
  ) => {
    const earnRuleId = _get(productDetails, 'baseOptions[0].options[0].priceData.earnRuleId') || '';
    const superCashEarning =
      _get(productDetails, 'baseOptions[0].options[0].priceData.superCashEarning') || '';

    // Size + pincode → scopedView price. Keep prior scoped price while a new
    // request is in flight (selectedSlaOption is not cleared on REQUEST).
    const slaPriceData = selectedSlaOption?.price?.priceData;
    const slaWasPriceData = selectedSlaOption?.price?.wasPriceData;
    const disPriceFromSla = formatSlaOptionPrice(slaPriceData);
    if (disPriceFromSla) {
      const origPrice = formatSlaOptionPrice(slaWasPriceData);
      const disPercentage =
        slaPriceData?.discountPercent ||
        (slaWasPriceData?.value != null && slaPriceData?.value != null
          ? calculateDiscount(slaWasPriceData.value, slaPriceData.value)
          : '');
      return {
        discountPrice: disPriceFromSla,
        originalPrice: disPriceFromSla !== origPrice ? origPrice : '',
        discountPercentage: disPriceFromSla !== origPrice ? disPercentage : '',
        isBothPriceSame: disPriceFromSla === origPrice,
        superCashEarning: slaPriceData?.superCashEarning || superCashEarning,
        earnRuleId: slaPriceData?.earnRuleId || earnRuleId
      };
    }

    const { price: { value } = {}, wasPriceData: { value: totalValue } = {} } = productDetails;
    // While scopedView is in flight with no prior option, keep product-level
    // price — do not flash size-variant aggregator from SELECT_SIZE.
    if (isSlaFetching) {
      const disPrice = displayValue(value);
      const origPrice = displayValue(totalValue);
      const disPercentage = calculateDiscount(totalValue, value);
      return {
        discountPrice: disPrice,
        originalPrice: disPrice !== origPrice ? origPrice : '',
        discountPercentage: disPrice !== origPrice ? disPercentage : '',
        isBothPriceSame: disPrice === origPrice,
        superCashEarning,
        earnRuleId
      };
    }

    const disPrice = discountPrice || displayValue(value);
    const origPrice = originalPrice || displayValue(totalValue);
    const disPercentage = discountPercentage || calculateDiscount(totalValue, value);
    return {
      discountPrice: disPrice,
      originalPrice: disPrice !== origPrice ? origPrice : '',
      discountPercentage: disPrice !== origPrice ? disPercentage : '',
      isBothPriceSame: disPrice === origPrice,
      superCashEarning,
      earnRuleId
    };
  }
);

// data available after firing actions
const getSimilarStylesDetails = createSelector(getProduct, ({ similarData = {} }) => {
  const { similarProducts = [] } = similarData;
  return getProductProps(similarProducts);
});

const getCrossSellDetails = createSelector(getProduct, ({ crossSellData = [] }) => {
  // const { products = [] } = crossSellData;
  // return getProductProps(products);
  return crossSellData;
});

const getSimilarStylesDetailsWithLimit = state => {
  const similarProducts = getSimilarStylesDetails(state);
  const similarProductsLength = similarProducts?.length || 0;
  if (similarProductsLength > 0) {
    const similarDataList = similarProducts;
    const similarDataMaxLimit = getSimilarProductsMaxLimit(state);
    if (similarDataMaxLimit > 0 && similarDataMaxLimit < similarProductsLength) {
      const newSimilarDataList = similarDataList.slice(0, similarDataMaxLimit);
      return newSimilarDataList;
    }
    return similarProducts;
  }
  return similarProducts;
};
const getSimilarStylesDetailsExitIntent = state => {
  const similarProducts = getSimilarStylesDetails(state);
  const similarProductsLength = similarProducts?.length || 0;
  if (similarProductsLength > 0) {
    const newSimilarDataList = similarProducts.slice(0, 15);
    return newSimilarDataList;
  }
  return similarProducts;
};

const getRecentlyViewsDetails = createSelector(getProduct, product => {
  const { products = [] } = product?.recentlyViewed || {};
  return getProductProps(products);
});

const getShopTheLook = createSelector(getProduct, product => product.ensamble);
const getShopTheCapsule = createSelector(getProduct, product => product.capsules);
const getRecommendSize = createSelector(
  getProduct,
  product => product?.sizeRecommendData?.sizeName || ''
);

export const getProductDetails = createSelector(getProduct, product => product?.productDetails);

const getProductData = createSelector(getProduct, product => product?.productDetails);

const getPreviousSearchSource = createSelector(getProduct, product => product?.previousSource);
const getPageCategory = createSelector(getProduct, product => product?.pageCategory);

const getSelectedSizeDetails = createSelector(
  getProduct,
  ({ selectedSizeCode, selectedSizeName, selectedSizeStockLevel }) => ({
    selectedSizeCode,
    selectedSizeName,
    selectedSizeStockLevel
  })
);

const getSegmentId = createSelector(
  getProductDetails,
  productDetails => productDetails?.price?.segmentId || ''
);

const getExperimentId = createSelector(
  getProductDetails,
  productDetails => productDetails?.price?.experimentId || ''
);

const getbrandName = createSelector(
  getProductDetails,
  productDetails => productDetails?.fnlColorVariantData?.brandName || ''
);

const getBrickName = createSelector(
  getProductDetails,
  productDetails => productDetails?.brickName || ''
);

const getBrickCategory = createSelector(
  getProductDetails,
  productDetails => productDetails?.brickCategory || ''
);

const getBrickCode = createSelector(
  getProductDetails,
  productDetails => productDetails?.brickCode || ''
);

const getSectionOne = createSelector(
  getProductDetails,
  productDetails => productDetails?.sectionOne
);

const getSectionTwo = createSelector(
  getProductDetails,
  productDetails => productDetails?.sectionTwo
);
const getSectionThree = createSelector(
  getProductDetails,
  productDetails => productDetails?.sectionThree
);

const getJioAddData = createSelector(getProductDetails, productDetails => {
  const {
    brickCategory: segment,
    brickSubCategory: vertical,
    brickName: brickname,
    code: id,
    price = {}
  } = productDetails;
  console.log('TRACK JIO ADS OBSERVATION POINT 4 ~ productDetails:', productDetails);
  const { value = 0 } = price;
  return {
    productDetails: {
      currency: 'INR',
      startTime: '',
      endTime: '',
      productList: [
        {
          segment,
          vertical,
          brickname,
          id,
          price: value,
          quantity: 1
        }
      ]
    }
  };
});

const getTrustMarkerPdp = createSelector(
  getProductDetails,
  productDetails => productDetails?.fnlProductData?.trustMarkerTxt || ''
);

const getProductColor = createSelector(
  getProductDetails,
  productDetails => productDetails?.fnlColorVariantData?.color || ''
);

const getCatDescription = createSelector(
  getProductDetails,
  productDetails => productDetails?.fnlColorVariantData?.catDescription || ''
);

const getAboutBrandImgUrl = createSelector(
  getProductDetails,
  productDetails => productDetails?.fnlColorVariantData?.aboutBrandImgUrl || ''
);

const getCategoryUrl = createSelector(
  getProductDetails,
  productDetails => productDetails?.fnlColorVariantData?.categoryUrl || ''
);

const getExclusiveTill = createSelector(
  getProductDetails,
  productDetails => productDetails?.fnlColorVariantData?.exclusiveTill
);

const getProductTag = createSelector(
  getProductDetails,
  productDetails => productDetails?.fnlColorVariantData?.productTag || ''
);

const getIsComingSoonProduct = createSelector(
  getProductDetails,
  productDetails => productDetails?.isComingSoonProduct || false
);

const getProductTagFromTagEngine = createSelector(getProductDetails, productDetails => {
  const { tagResponse: { tag: { categoryTags = [] } = {} } = {} } = productDetails;
  return getTagValueFromTagEngine(categoryTags);
});

const getWishlistedCountFromTagEngine = createSelector(getProductDetails, productDetails => {
  const { tagResponse: { tag: { categoryTags = [] } = {} } = {} } = productDetails;
  return getWishlistCountFromTagEngine(categoryTags);
});

const getPDPTopLeftProductTag = state => {
  const urgencyDriverV2PDPEnable = getUrgencyDriverV2PDPEnable(state);
  const PDPTopLeftProductTagV1 = getProductTagFromTagEngine(state);
  const PDPTopLeftProductTagV2 = getPDPTopLeftProductTagV2(state);
  if (urgencyDriverV2PDPEnable) return PDPTopLeftProductTagV2?.shortText || PDPTopLeftProductTagV1;
  return PDPTopLeftProductTagV1;
}

const getPDPTopLeftProductTagV2 = createSelector(getProductDetails, productDetails => {
  const { urgencyTagResponse: { tags = [] } = {}, code = '' } = productDetails;
  const productTag = tags.find(x => x.optionCode === code) || {};
  return getPDPTagV2FromPosition(productTag, 'PDP_BANNER_TOP_LEFT');
});

const getPDPBelowPriceProductTag = state => {
  const urgencyDriverV2PDPEnable = getUrgencyDriverV2PDPEnable(state);
  const PDPBelowPriceProductTagV1 = getSellingFastLableFromTagEngine(state?.product?.productDetails || {});
  const PDPBelowPriceProductTagV2 = getPDPBelowPriceProductTagV2(state);
  if (urgencyDriverV2PDPEnable) return PDPBelowPriceProductTagV2?.shortText || PDPBelowPriceProductTagV1;
  return PDPBelowPriceProductTagV1;
}

const getPDPTrendsVibes = createSelector(getProductDetails, productDetails => {
  const { urgencyTagResponse = {} } = productDetails;
  return urgencyTagResponse;
});

const getPDPBelowPriceProductTagV2 = createSelector(getProductDetails, productDetails => {
  const { urgencyTagResponse: { tags = [] } = {}, code = '' } = productDetails;
  const productTag = tags.find(x => x.optionCode === code) || {};
  return getPDPTagV2FromPosition(productTag, 'PDP_BELOW_PRICE');
});

const getProductPriceLabelFromTagEngine = createSelector(getProductDetails, productDetails => {
  const { tagResponse: { tag: { categoryTags = [] } = {} } = {} } = productDetails;
  return getPriceLableFromTagEngine(categoryTags);
});

const getEDD = (state: import('../types/rootState').RootState): import('../types/edd').Edd =>
  state.edd;

const getEddDetails = createSelector(
  getEDD,
  getSelectedSizeDetails,
  (edd, { selectedSizeCode }) => {
    const { activePincode, eddDetails } = edd;
    const pincodedata = eddDetails[activePincode];
    if (pincodedata) {
      return { ...pincodedata[selectedSizeCode], activePincode };
    }
    return {};
  }
);
const getServiceabilityDetailsDetails = createSelector(
  getEDD,
  (edd) => {
    const { serviceabilityDetails = {} } = edd;
    return serviceabilityDetails;
  }
);
const getEDDFetchingFlag = createSelector(getEDD, edd => edd.isEDDFetching);
const getServiceabilityFetchingFlag = createSelector(getEDD, edd => edd.isServiceabilityFetching);
const getIsCheckServiceabilityCalled = createSelector(getEDD, edd => edd.isCheckServiceabilityCalled);
const getIsCheckDeliveryStatusCalled = createSelector(getEDD, edd => edd.isCheckDeliveryStatusCalled);
const getIsCheckServiceabilityCalledForRex = createSelector(getEDD, edd => edd.isCheckServiceabilityCalledForRex);

const getErrorInServiceabilityFetching = createSelector(getEDD, edd => edd.errorInServiceabilityFetching)
const getSizeColorVariant = createSelector(getProductDetails, extractVariantsFromProduct);

const getStockLevelStatus = createSelector(
  getSizeColorVariant,
  getOosStatus,
  ({ sizeVariant = [] }, oosStatus) => {
    let stockLevelStatus = 'outOfStock';
    sizeVariant.forEach(({ stock, code }) => {
      if (stock?.stockLevelStatus !== 'outOfStock' && !oosStatus?.includes(code)) {
        stockLevelStatus = stock?.stockLevelStatus;
      }
    });
    return stockLevelStatus;
  }
);

const getIsOutOfStock = createSelector(
  getStockLevelStatus,
  stockLevelStatus => stockLevelStatus === 'outOfStock'
);

const getIsNotServicable = createSelector(getEDD, getStockLevelStatus, (edd, stockLevelStatus) => {
  let reasonForNotServiceability;
  const { activePincode, eddDetails } = edd;
  const prductEDD = eddDetails[activePincode];
  if (prductEDD && prductEDD.length) {
    reasonForNotServiceability = prductEDD.reasonForNotServiceability;
  }
  if (reasonForNotServiceability == 'OOS' || stockLevelStatus === 'outOfStock') {
    return true;
  }
  return false;
});

const getAddToCartProps = createSelector(
  getSelectedSizeDetails,
  isAddingTocart,
  getOosStatus,
  getIsNotServicable,
  getAddToCartState,
  getErrorMessage,
  getSis,
  isAddingTocartMembership,
  getAddToCartStateForMembership,
  (
    { selectedSizeCode },
    loading,
    oosStatus,
    isNotServicable,
    { addedToCart, showCartPopup, sellerChanged },
    errorMessage,
    isStore,
    loadingMembership,
    addedToCartMembership
  ) => {
    const isOutofStock = oosStatus?.includes(selectedSizeCode);
    const btnlabel = addedToCart
      ? isStore && isStore === 'luxe'
        ? 'View Bag'
        : 'Go To Bag'
      : 'Add To Bag';
    return {
      addingToBag: loading,
      variant: addedToCart ? 'secondary' : 'primary',
      isOutOfStock: isOutofStock || isNotServicable,
      label: isOutofStock || isNotServicable ? 'Out Of Stock' : btnlabel,
      errorMessage,
      addedToCart,
      showCartPopup,
      sellerChanged,
      loadingMembership,
      addedToCartMembership,
      variantMembership: addedToCartMembership ? 'secondary' : 'primary',
    };
  }
);

function insertArrayAtIndex(targetArray, insertArray, index) {
  targetArray.splice(index, 0, ...insertArray);
  return targetArray;
}

const getProductImages = createSelector(getProductDetails, productDetails => {
  const {productVideos = {},  images = []} =  productDetails;
  let productCarouselBar = images; 
  if(productVideos && Array.isArray(productVideos.videos) && productVideos.videos.length > 0) {
    const filteredImages = images.filter(image => 
      (image.format === 'product' || image.format === 'MP4') && image.imageType !== 'PRIMARY'
    );
    productCarouselBar = insertArrayAtIndex(filteredImages, productVideos.videos, productVideos.videoPosition);
  }
  return productCarouselBar.reduce<import('../types/product-page-types').ImageObj[]>((acc, image, index) => {
    if ((image.format === 'product' || image.format === 'MP4') && image.imageType !== 'PRIMARY') {
      return [
        ...acc,
        {
          id: index,
          slideItem: image.url,
          slideDesc: image.altText,
          isVideo: image.format === 'MP4',
          thumbnailImage: image?.thumbnail?.url ?? ''
        }
      ];
    }
    return acc;
  }, [] as import('../types/product-page-types').ImageObj[]);
});

const getSpotlightData = createSelector(getProductDetails, productDetails => {
  const { spotlight = {} } = productDetails;
  const { spotLightAttributes = [] } = spotlight;
  return spotLightAttributes || [];
});

const getThumbnailImage = createSelector(getProductDetails, productDetails => {
  const { images = [] } = productDetails;
  let imageUrl = '';
  images.find(item => {
    if (item.format === 'thumbnail' && item.imageType !== 'PRIMARY') {
      imageUrl = item.url;
    }
  });
  return imageUrl;
});

const getProductZoomImages = createSelector(getProductDetails, productDetails => {
  const { images = [] } = productDetails;
  return images.reduce<import('../types/product-page-types').ImageObj[]>((acc, image, index) => {
    if (image.format === 'superZoomPdp' && image.imageType !== 'PRIMARY') {
      return [
        ...acc,
        {
          id: index,
          slideItem: image.url,
          slideDesc: image.altText
        }
      ];
    }
    return acc;
  }, [] as import('../types/product-page-types').ImageObj[]);
});

const getProductMarketPdp = createSelector(getProductDetails, productDetails => {
  const { images = [] } = productDetails;
  let imageUrl = '';
  images.forEach(item => {
    if (item.format === 'product') {
      imageUrl = item.url;
    } else if (item.format === 'productGrid3ListingImage') {
      imageUrl = item.url;
    } else if (item.format === 'mobileProductListingImage') {
      imageUrl = item.url;
    }
  });
  return imageUrl;
});

const getTrustMarkerImages = createSelector(getProductDetails, productDetails => {
  const { images = [] } = productDetails;
  const trustMarkerImages = images.reduce<import('../types/product-page-types').TrustMarker[]>(
    (acc, image, index) => {
      if (image.imageType === 'TRUST_MARKER') {
        return [
          ...acc,
          {
            id: index,
            url: image.url,
            altText: image.altText
          }
        ];
      }
      return acc;
    },
    [] as import('../types/product-page-types').TrustMarker[]
  );
  return trustMarkerImages.slice(0, 3);
});

const getProductName = createSelector(getProductDetails, productDetails => productDetails?.name);
const getProductCode = createSelector(getProductDetails, productDetails => productDetails?.code);
const getProductUrl = createSelector(getProductDetails, productDetails => productDetails?.url);
const getProductCatalog = createSelector(
  getProductDetails,
  productDetails => productDetails?.catalog
);
const getProductCategory = createSelector(
  getProductDetails,
  productDetails => productDetails?.brickCategory
);
const getProductVertical = createSelector(
  getProductDetails,
  productDetails => productDetails?.verticalColor || ''
);
const getProductSubCategory = createSelector(
  getProductDetails,
  productDetails => productDetails?.brickSubCategory || ''
);

const hideColumnInToolTip = (key = ''): boolean => {
  const arr = ['universal size format', 'brand size format', 'universal size', 'brand size'];
  return arr.every(item => !key.toLowerCase().includes(item));
};

const getSizeRevampFlag = createSelector(
  getNewSizeRevampFlag,
  newSizeRevampFlag => newSizeRevampFlag
);

const getDisableGstFlag = createSelector(
  getDisableGSTContentFlag,
  disableGSTContent => disableGSTContent
);

const getDefaultGstText = createSelector(
  getDefaultGSTMessage,
  defaultGSTMessage => defaultGSTMessage
);

// const getPriceRevealFeatureFlag = createSelector(
//   getPriceRevealMobileFeatureFlag,
//   priceRevealMobileFeatureFlag => priceRevealMobileFeatureFlag
// );

const getPriceRevealV2FeatureFlag = createSelector(
  getPriceRevealV2MobileFeatureFlag,
  priceRevealV2MobileFeatureFlag => priceRevealV2MobileFeatureFlag
);

const getShoppingAssistantLuxeFlag = createSelector(
  getShoppingAssistantFlag,
  shoppingAssistantFlag => shoppingAssistantFlag
);

const getPriceRevealFlagPDP = createSelector(
  getPriceRevealMobileData,
  data => data?.priceRevealPDPFlag === 'ON'
);

const getToolTip = createSelector(
  getProductDetails,
  getNewSizeRevampFlag,
  (productDetails, newSizeRevampFlag) => {
    const sizeToolTip = productDetails?.fnlColorVariantData?.sizeToolTip;
    let tooltipObject = [];
    const unit = {
      '': '"',
      '0': '',
      '1': 'in'
    };
    if (sizeToolTip) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        tooltipObject = JSON.parse(sizeToolTip);
      } catch (err) {
        console.log('error', err);
      }
    }
    return tooltipObject.reduce((acc, cur) => {
      let attributeAdded = 0;
      let tooltip = [];
      const noOfAttributes =
        cur.Show_Tool_Tip && cur.Show_Tool_Tip !== 'no' && cur.Show_Tool_Tip !== '0'
          ? cur.Show_Tool_Tip
          : 2;
      Object.keys(cur).forEach((key, index) => {
        if (
          key.includes('_attribute') &&
          attributeAdded < noOfAttributes &&
          cur[key] &&
          hideColumnInToolTip(key)
        ) {
          attributeAdded += 1;
          tooltip = [
            ...tooltip,
            `${key.split('_')[0]} ${cur[key]}${unit[cur.MEASUREMENT_UNIT || '']}`
          ];
        }
      });
      acc[cur.SIZE_NAME] = tooltip.join(' | ');
      return acc;
    }, {});
  }
);

const getToolTipMesg = createSelector(getProductDetails, productDetails => {
  const sizeToolTip = productDetails?.fnlColorVariantData?.sizeToolTip;
  let tooltipObject = [];
  if (sizeToolTip) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      tooltipObject = JSON.parse(sizeToolTip);
    } catch (err) {
      console.log('error', err);
    }
  }
  return tooltipObject.reduce((acc, cur) => {
    acc[cur.SIZE_NAME] = cur.DESCRIPTION ?? '';
    return acc;
  }, {});
});

// fetch product standard size
const standardSizeFormate = createSelector(getProductDetails, getStandardSize);

// fetch featured standard size from featureData key in product details
const featureProductData = createSelector(getProductDetails, getBrandDetails);

const getBrandSizes = createSelector(getProductDetails, ({ fnlColorVariantData = {} }) => {
  const { sizeGuideDesktop } = fnlColorVariantData;

  let sizeGuideDesktopJson = {};

  if (sizeGuideDesktop) {
    sizeGuideDesktopJson = JSON.parse(sizeGuideDesktop);
  }

  const { sizechart = [] } = sizeGuideDesktopJson;
  const sizeArr: any = [];

  const sizeChartData = sizechart[0] ? sizechart[0].brickBrandSizes : [];
  sizeChartData &&
    sizeChartData.map((val, i) => {
      let universalSize = '';
      let brandValue = '';
      const { sizeChartAttributes } = val;
      const obj = {};
      sizeChartAttributes.map((val1, i1) => {
        if (val1.attributeName === 'Universal Size_attribute') {
          universalSize = val1.attributeValue;
          Object.assign(obj, { universalSize });
        }
        if (val1.attributeName === 'Brand Size_attribute') {
          brandValue = val1.attributeValue;
          Object.assign(obj, { brandValue, sizeName: val.sizeName });
        }
      });
      const isObjEmpty = Object.keys(obj).length === 0 && obj.constructor === Object;
      !isObjEmpty && sizeArr.push(obj);
    });

  return sizeArr;
});

const getSizeData = createSelector(
  featureProductData,
  standardSizeFormate,
  getSizeColorVariant,
  getToolTip,
  getNewSizeRevampFlag,
  getOosStatus,
  getHurryMsgInvCap,
  getToolTipMesg,
  (
    { featureStandardFormate },
    { displaySizeFormate },
    { sizeVariant = [], colorVariant },
    sizeToolTip = {},
    newSizeRevampFlag,
    oosStatus,
    hurryMsgInvCap = 10,
    sizeToolTipMesg = {}
  ) => {
    const displaystandardFormate = displaySizeFormate;
    const { appendDesc, hideSizes, qualifierKey } = extractSelectionValues(colorVariant);
    const sizeData = sizeVariant.reduce(
      (
        acc,
        {
          stock: { stockLevel, stockLevelStatus } = {},
          priceData: { value, deal, superCashEarning, earnRuleId },
          wasPriceData: { value: totalValue },
          code,
          variantOptionQualifiers,
          scDisplaySize
        }
      ) => {
        // const displayVariationStandardSize = variantOptionQualifiers.reduce((res, data) => (data.name === 'Standard Size' || data.qualifier === 'standardSize' && res.push(data.value), res), [])[0]
        const displayVariationSize = variantOptionQualifiers.reduce(
          (res, data) => (
            data.name === 'Size' ||
              data.qualifier === 'size' ||
              data.name === 'Brand Size' ||
              (data.qualifier === 'brandSize' && res.push(data.value)),
            res
          ),
          []
        )[0];

        const sizeName = getSizeName({
          variantOptionQualifiers,
          newSizeRevampFlag,
          scDisplaySize,
          appendDesc,
          qualifierKey
        });

        let stockMessage = '';
        if (stockLevel && stockLevel <= hurryMsgInvCap && stockLevel > 0) {
          stockMessage = 'Hurry, Only few items left';
        }
        if (stockLevelStatus === 'lowStock') {
          stockMessage = `Hurry, Only ${stockLevel} Items Left`;
        }

        // Show brand size based on below condition
        // if feature standard size is not qual to display standard size then show the brand size
        let brandSize;
        if (featureStandardFormate && displaystandardFormate !== featureStandardFormate.value) {
          if (typeof displayVariationSize === 'undefined') brandSize = featureStandardFormate.value;
          else brandSize = `${featureStandardFormate.value}-${displayVariationSize}`;
        }

        return {
          data: {
            ...acc.data,
            [sizeName]: {
              stockLevel,
              stockLevelStatus,
              code,
              sizeName,
              discountPrice: displayValue(value),
              originalPrice: displayValue(totalValue),
              discountPercentage: calculateDiscount(totalValue, value),
              tooltip: sizeToolTip[sizeName],
              tooltipMesg: sizeToolTipMesg[sizeName],
              deal,
              stockMessage,
              brandSize,
              superCashEarning,
              earnRuleId
            }
          },
          sizes: [
            ...acc.sizes,
            {
              value: sizeName,
              label: sizeName,
              disabled: stockLevelStatus === 'outOfStock' || oosStatus?.includes(code)
            }
          ]
        };
      },
      {
        sizes: [],
        data: {},
        standardFormate: undefined
      }
    );
    sizeData.hideSizes = hideSizes;
    sizeData.showSizeinDesc = appendDesc;
    sizeData.standardFormate = displaystandardFormate;
    return sizeData;
  }
);

const getGstText = createSelector(
  getProductDetails,
  getSizeColorVariant,
  getDisableGstFlag,
  getDefaultGstText,
  (productDetails, { colorVariant }, disableGSTContent = '', defaultGSTMessage = '') => {
    const value = productDetails?.price?.taxInformation?.priceWithTaxes?.value;
    const { jewelleryProduct } = extractSelectionValues(colorVariant);

    if (disableGSTContent === 'ON') {
      return defaultGSTMessage;
    }
    if (jewelleryProduct) {
      return '';
    }
    return value && value > 0 ? `${displayValue(value)} including GST` : 'Additional GST may apply';
  }
);

const getOffersCount = createSelector(
  getProductDetails,
  productDetails => productDetails?.potentialPromotions.length
);

// Resolves the variantOption matching the currently selected size code.
// On first load (no size selected) this returns undefined so callers fall
// back to product-level offers/emi; on size selection it exposes that
// variant's own `vouchers` and `emi` payloads.
const getSelectedVariantOptions = createSelector(
  getProductDetails,
  getSelectedSizeDetails,
  (productDetails, { selectedSizeCode }) =>
    selectedSizeCode
      ? (productDetails?.variantOptions || []).find(variant => variant?.code === selectedSizeCode)
      : undefined
);

const getOffersDetails = createSelector(
  [
    getProductDetails,
    getSelectedVariantOptions,
    getEmiDiscountEnable,
    getSelectedSlaOption,
    getIsSlaFetching
  ],
  (productDetails, selectedVariant, emiDiscountEnable, selectedSlaOption, isSlaFetching) => {
    // Prefer last scopedView option while a new scopedView request is in flight —
    // never flash to size-variant aggregator mid-fetch.
    let potentialPromotions;
    if (selectedSlaOption) {
      potentialPromotions = selectedSlaOption.vouchers || [];
    } else if (isSlaFetching) {
      potentialPromotions = productDetails.potentialPromotions || [];
    } else if (selectedVariant?.vouchers?.length) {
      potentialPromotions = selectedVariant.vouchers;
    } else {
      potentialPromotions = productDetails.potentialPromotions || [];
    }

    return (potentialPromotions || []).map(
      ({
        title,
        termsLink = '',
        maxSavingPrice,
        description,
        restrictedToNewUser,
        endTime,
        timerShowDuration,
        showEndTimer,
        serverTime,
        code
      }) => ({
        tncUrl: termsLink.split('|')[1]?.length > 0 ? termsLink.split('|')[1] : termsLink,
        price: maxSavingPrice,
        // Variant/scopedView vouchers can carry an empty title; fall back to code.
        couponCode: title || code,
        description,
        restrictedToNewUser,
        endTime,
        timerShowDuration,
        showEndTimer,
        serverTime,
        code
      })
    );
  }
);

const getBankCouponsFromPDPAggregator = createSelector(getProductDetails, productDetails => {
  const { prepaidOffers = [] } = productDetails;
  return prepaidOffers?.map(({ description = '', logo = '', bankId = '', tncUrl = '' }) => ({
    description,
    bankLogo: logo,
    bankId,
    tncUrl
  }));
});

const getBankCoupons = createSelector(getProduct, product => {
  const { bankOffers = [] } = product;
  return bankOffers?.map(({ description = '', logo = '', bankId = '', tncUrl = '' }) => ({
    description,
    bankLogo: logo,
    bankId,
    tncUrl
  }));
});

const youMayAlsoLike = createSelector(getProduct, data => {
  return data;
});

const getMoreLinks = createSelector(getProductDetails, productDetails => {
  const {
    fnlColorVariantData: { brandName = '', color = '' } = {},
    styleType = '',
    rilfnlBreadCrumbList: { rilfnlBreadCrumb } = {}
  } = productDetails;

  const colorList = ['red', 'green', 'blue', 'black', 'white', 'grey'];
  const styleTypes = ['stylized', 'stylised'];
  const { url = '', name = '' } = rilfnlBreadCrumb ? rilfnlBreadCrumb[2] : {};
  return {
    name,
    data: [
      {
        text: `All ${name}`,
        link: url
      },
      ...(colorList.includes(color.toLowerCase())
        ? [
            {
              text: `More ${color} ${name}`,
              link: `${url}?query=:relevance:verticalcolorfamily:${color}`
            }
          ]
        : []),
      ...(!styleTypes.includes(styleType.toLowerCase())
        ? [
            {
              text: `More ${styleType} ${name}`,
              link: `${url}?query=:relevance:brickstyletype:${styleType.replace('&', '%26')}`
            }
          ]
        : []),
      {
        text: `More ${name} by ${brandName}`,
        link: `${url}?query=:relevance:brand:${brandName.replace('&', '%26')}`
      }
    ]
  };
});

const getMandatoryInfo = createSelector(
  getProductDetails,
  getSelectedSizeDetails,
  getSizeColorVariant,
  getB2cSellerFlag,
  getSelectedSlaOption,
  (
    { mandatoryInfo: prodMandatoryInfo = [] },
    { selectedSizeCode },
    { sizeVariant = [], colorVariant },
    b2cSellerEnabled,
    selectedSlaOption
  ): import('../types/product-page-types').MandatoryInfo[] => {
    const { qualifierKeysForDesc } = extractSelectionValues(colorVariant);
    let productMandate: import('../types/product-page-types').MandatoryInfo[] = [];
    let skuDetails: import('../types/product-page-types').MandatoryInfo[] = [];
    const skuDetailsFrom = (
      variant?: import('../types/product-page-types').VariantOption
    ): import('../types/product-page-types').MandatoryInfo[] =>
      (variant?.variantOptionQualifiers || []).reduce<
        import('../types/product-page-types').MandatoryInfo[]
      >((acc, { name, qualifier, value }) => {
        if (qualifierKeysForDesc.includes(qualifier)) {
          acc.push({ key: name, title: value });
        }
        return acc;
      }, []);
    /**
     * B2C: one source. scopedView option when it has landed; otherwise the
     * catalogue size / colour. Never concatenated with product-level info —
     * that doubled the rows once pincode / scopedView kicked in.
     */
    if (b2cSellerEnabled) {
      const scopedMandate =
        selectedSlaOption?.mandatoryInfo ??
        selectedSlaOption?.serviceabilityDetails?.productDetails?.[0]?.mandatoryInfo;
      if (Array.isArray(scopedMandate)) {
        return scopedMandate;
      }
      const { mandatoryInfo, variant } = resolveB2cMandatoryInfo(
        colorVariant,
        sizeVariant,
        selectedSizeCode
      );
      return [...skuDetailsFrom(variant), ...mandatoryInfo];
    }
    for (let idx = 0; idx < sizeVariant.length; idx += 1) {
      const { code = '', mandatoryInfo = [], variantOptionQualifiers } = sizeVariant[idx];
      if (!selectedSizeCode) {
        productMandate = mandatoryInfo;
        break;
      }
      if (code === selectedSizeCode) {
        skuDetails = variantOptionQualifiers.reduce<
          import('../types/product-page-types').MandatoryInfo[]
        >((acc, { name, qualifier, value }) => {
          const info: import('../types/product-page-types').MandatoryInfo = {
            title: '',
            key: ''
          };
          if (qualifierKeysForDesc.includes(qualifier)) {
            info.key = name;
            info.title = value;
            acc.push(info);
          }
          return acc;
        }, []);
        productMandate = mandatoryInfo;
        break;
      }
    }
    return [...skuDetails, ...productMandate, ...prodMandatoryInfo];
  }
);

/**
 * Seller for the B2C PDP with no delivery location — the picked size's own
 * catalogue variant.
 *
 * Empty in the two cases the row must stay hidden: with no size picked there is
 * no seller to name (they fulfil a size, not a product), and with a pincode the
 * scopedView response names it instead — that answer can differ from the
 * catalogue's, so the SLA card owns the row there.
 */
const getB2cNoLocationSellerName = createSelector(
  getSelectedSizeDetails,
  getSizeColorVariant,
  getIsB2cNoLocation,
  ({ selectedSizeCode }, { sizeVariant = [] }, isB2cNoLocation): string =>
    isB2cNoLocation ? resolveB2cSellerName(sizeVariant, selectedSizeCode) : ''
);

const disableFeatureDataList = ['Pantone Shade', 'Size Format'];
const modelSizeDetails = [
  'Size worn by Model',
  'Model Chest Size',
  'Model Waist Size',
  'Model Height'
];

function getModelSizeDescription(
  SizeWornByModel: string,
  ModelChestSize: string,
  ModelHeight: string,
  ModelWaistSize: string
): string {
  if (SizeWornByModel) {
    let innText = `Our model wears a Size ${SizeWornByModel}`;
    if (ModelChestSize || ModelHeight || ModelWaistSize) {
      innText = `${innText} with`;
    }
    if (ModelHeight) {
      innText += ` Height ${ModelHeight}`;
    }
    if (ModelChestSize) {
      if (ModelWaistSize) {
        innText += `, Chest ${ModelChestSize} and Waist ${ModelWaistSize}`;
      } else {
        innText += `and Chest ${ModelChestSize}.`;
      }
    } else if (ModelWaistSize) {
      innText += `and Waist ${ModelWaistSize}.`;
    }
    return innText;
  }
  return '';
}
const getFeatureData = createSelector(
  getProductDetails,
  getProductCode,
  getSelectedSizeDetails,
  (productDetails, productCode, { selectedSizeCode }) => {
    const { featureData = [] } = productDetails;
    let SizeWornByModel = '';
    let ModelChestSize = '';
    let ModelHeight = '';
    let ModelWaistSize = '';
    const featureDataArr = featureData.reduce<string[]>((acc, curr) => {
      if (disableFeatureDataList.includes(curr.name)) {
        return acc;
      }
      if (modelSizeDetails.includes(curr.name)) {
        switch (curr.name) {
          case 'Size worn by Model':
            SizeWornByModel = curr.featureValues[0].value;
            break;
          case 'Model Chest Size':
            ModelChestSize = curr.featureValues[0].value;
            break;
          case 'Model Height':
            ModelHeight = curr.featureValues[0].value;
            break;
          case 'Model Waist Size':
            ModelWaistSize = curr.featureValues[0].value;
            break;
          default:
        }
        return acc;
      }
      const featureValues: string[] = [];
      curr.featureValues.forEach(feature => {
        if (feature.value !== 'UNI') {
          featureValues.push(feature.value);
        }
      });
      return [...acc, ...featureValues];
    }, [] as string[]);
    featureDataArr.push(`Product Code: ${selectedSizeCode || productCode}`);
    const modelDescription = getModelSizeDescription(
      SizeWornByModel,
      ModelChestSize,
      ModelHeight,
      ModelWaistSize
    );
    return { featureList: featureDataArr, modelDescription };
  }
);

const getSnekaerHoodData = createSelector(getProductDetails, productDetails => {
  const { featureData = [] } = productDetails;
  const arr = [];
  featureData?.map(list => {
    const value = list?.featureValues[0]?.value;
    arr.push({ key: list?.name, title: value });
  });
  return { featuredData: arr };
});

const getComprehensiveProductData = createSelector(getProductDetails, productDetails => {
  const { comprehensiveProductDetails: { images = [], videos = [] } = {} } = productDetails;
  const comprehensiveData = _sortBy([...images, ...videos], 'kypSequence');
  return comprehensiveData;
});

const getReturnDetails = createSelector(
  getProductDetails,
  ({ returnContent, returnUrl, returnTitle }) => ({ returnContent, returnUrl, returnTitle })
);

const getSwatchColors = createSelector(getSizeColorVariant, ({ colorVariant }) => {
  const { options = [], selected: { code: selectedColor = '' } = {} } = colorVariant || {};
  const { metalPurity } = extractSelectionValues(colorVariant);
  const colorArray: string[] = [];
  const colorDetail: import('../types/product-page-types').SwatchColorType[] = [];
  const metalDetail: import('../types/product-page-types').SwatchColorType[] = [];
  let selctedColorName = '';
  let selectedMetalName = '';
  options.forEach(color => {
    let colorSelected = false;
    if (color.code === selectedColor) {
      colorSelected = true;
    }
    color.variantOptionQualifiers.forEach(colorSwatch => {
      const { swatchImage } = colorSwatch;
      const { url = '' } = swatchImage || {};
      if (colorSwatch.name === 'Color' && url) {
        colorArray.push(url);
        colorDetail.push({
          code: color.code,
          productUrl: color.url,
          url,
          value: colorSwatch.value,
          colorSelected,
          colorName: color?.color,
          productImageUrl: color?.modelImage?.url,
          stockLevel: color?.stock?.stockLevel,
          price: color?.priceData?.displayformattedValue
        });
        if (colorSelected) {
          selctedColorName = colorSwatch.value || '';
        }
      } else if (metalPurity && colorSwatch.qualifier === 'metalPurity') {
        metalDetail.push({
          code: color.code,
          productUrl: color.url,
          url,
          value: colorSwatch.value,
          label: colorSwatch.value,
          colorSelected
        });
        if (colorSelected) {
          selectedMetalName = colorSwatch.value || '';
        }
      }
    });
  });

  const totalColors = colorArray.length;
  const colorsText = totalColors > 0 ? `${totalColors} Color${totalColors > 1 ? 's' : ''}` : '';

  return {
    colorArray,
    colorDetail,
    selctedColorName,
    metalPurity,
    colorsText,
    metalDetail,
    selectedMetalName
  };
});

const getSizeRecommendFetch = createSelector(
  getProductDetails,
  ({
    fnlColorVariantData = {},
    baseProduct = ' ',
    seasonCode = ' ',
    brickCategory = ' ',
    fnlProductData = {}
  }) => {
    const { sizeGuideDesktop } = fnlColorVariantData;

    let sizeGuideDesktopJson = {};

    if (sizeGuideDesktop) {
      sizeGuideDesktopJson = JSON.parse(sizeGuideDesktop);
    }
    const {
      applicationName = ' ',
      brickCode = ' ',
      brandCode = ' ',
      vendorCode = ' ',
      fittingType = ' ',
      style = ' '
    } = sizeGuideDesktopJson;

    const { planningCategory = ' ' } = fnlProductData;
    return {
      application: applicationName,
      brick: brickCode,
      brand: brandCode,
      vendor: vendorCode.length ? vendorCode : ' ',
      fit: fittingType,
      style,
      productCode: baseProduct,
      seasonCode,
      gender: brickCategory,
      subCategory: planningCategory.toLowerCase() === 'clothing' ? 'YES' : 'NO'
    };
  }
);

const isSizeChartAvailable = createSelector(
  getProductDetails,
  productDetails => !!productDetails?.fnlColorVariantData?.sizeGuideDesktop
);

const getInitialDodData = createSelector(getProduct, ({ productDetails }) => {
  const { price: { deal = {} } = {} } = productDetails;

  const {
    isDealEnabled = false,
    isSoldOut = false,
    dealType = '',
    dealPrice = '',
    dealPercentage = '',
    premiumDealStartTime = '',
    actualDealStartTime = '',
    dealEndTime = '',
    dealCode = ''
  } = deal;

  const { isDealStart = false } = IsDealStart(actualDealStartTime);
  const { isDealEnd = true } = dealEndTime && IsDealEnd(dealEndTime);

  return {
    isDealEnabled,
    isSoldOut,
    dealType,
    dealPrice: displayValue(dealPrice),
    dealPercentage,
    premiumDealStartTime,
    actualDealStartTime,
    dealEndTime,
    dealCode,
    isDealStart,
    isDealEnd
  };
});

const getDodSelectedSize = createSelector(getProduct, ({ dodSize }) => dodSize);

const getServiceErrors = createSelector(
  getProductDetails,
  productDetails => productDetails?.serviceErrors || []
);

const getProductStock = createSelector(
  getProductDetails,
  productDetails => productDetails?.stock || {}
);

const getVariantOptions = createSelector(
  getProductDetails,
  productDetails => productDetails?.variantOptions || []
);

const getShouldDisplayPricingError = createSelector(
  getProductDetails,
  productDetails => productDetails?.shouldDisplayPricingError
);
const getErrorPriceRefresh = createSelector(
  getServiceErrors,
  getShouldDisplayPricingError,
  (serviceErrors = [], shouldDisplayPricingError) => {
    const codeArr = [];
    serviceErrors.forEach(err => {
      const { code } = err;
      const codeVal = code ? code.toString().slice(0, 3) : '';
      codeArr.push(codeVal);
    });
    const priceRefresh = codeArr.includes('902');
    return priceRefresh && shouldDisplayPricingError;
  }
);

const getPricebreakUp = createSelector(getSizeColorVariant, ({ sizeVariant = [] }) => {
  const priceBreakUpObject = sizeVariant.reduce((acc, { code, priceBreakUp }, index) => {
    acc[code] = priceBreakUp;
    return acc;
  }, {} as Record<string, import('../types/product-page-types').PriceBreakUp[]>);
  let hasPriceBreakUp = true;
  if (allUndefined(priceBreakUpObject)) {
    hasPriceBreakUp = false;
  }
  return {
    priceBreakUpObject,
    hasPriceBreakUp
  };
});

const getIsPriceRevealPDP = createSelector(
  getProductDetails,
  getPriceRevealV2FeatureFlag,
  getPriceRevealFlagPDP,
  getSis,
  getPriceRevealLUXEFlag,
  getIsOutOfStock,
  (
    productDetails,
    priceRevealFeatureFlag,
    priceRevealPDPFlag,
    isStore,
    isLuxeEnabled,
    isOutOfStock
  ) => {
    if (isStore && isStore === 'luxe') {
      return (
        !isOutOfStock &&
        isLuxeEnabled &&
        priceRevealFeatureFlag &&
        priceRevealPDPFlag &&
        productDetails?.saleStartTime &&
        productDetails?.saleEndTime
      );
    }
    return (
      priceRevealFeatureFlag &&
      priceRevealPDPFlag &&
      productDetails?.saleStartTime &&
      productDetails?.saleEndTime &&
      !isOutOfStock
    );
  }
);

const getIsShoppingAssistantPDP = createSelector(
  getShoppingAssistantLuxeFlag,
  getIsOutOfStock,
  (shoppingAssistantFlag, isOutOfStock) => shoppingAssistantFlag && !isOutOfStock
);

const getShoppingAssistantDetails = createSelector(
  getIsShoppingAssistantPDP,
  getShoppingAssistantData,
  (isShoppingAssist, { phoneNumber, time }) => {
    return isShoppingAssist ? { phoneNumber, time } : '';
  }
);

const getPriceRevealSaleDuration = createSelector(
  getProductDetails,
  getIsPriceRevealPDP,
  (productDetail, priceRevealPDPFlag) => {
    const { saleStartTime, saleEndTime } = productDetail || {}
    const currentTS = Date.now() / 1000;
    if (saleStartTime && saleEndTime && priceRevealPDPFlag) {
      if (currentTS >= saleStartTime && currentTS < saleEndTime) {
        return DURING_SALE;
      }
      if (currentTS < saleStartTime) {
        return PRE_BUZZ;
      }
    }
    return false;
  }
);

const getSaleStartEndText = createSelector(
  getPriceRevealSaleDuration,
  getPriceRevealMobileData,
  (saleDuration, { bbsSaleStartText, bbsSaleEndText }) => {
    return saleDuration === PRE_BUZZ ? bbsSaleStartText : bbsSaleEndText;
  }
);

const getPriceRevealData = createSelector(
  getIsPriceRevealPDP,
  getProductDetails,
  getPriceRevealMobileData,
  getSaleStartEndText,
  (
    isPriceReveal,
    { futureBestPrice, futureDiscountPercent, price, saleStartTime },
    {
      luxebbsPriceStaticText,
      bbsPriceStaticText,
      userEduText,
      luxeUserEduText,
      priceComingSoonTxt,
      luxePriceComingSoonTxt,
      priceImgSrc,
      luxePriceImgSrc,
      bannerImgSrc,
      luxeBannerImgSrc
    },
    saleStartEndText
  ) => {
    const priceValue = price?.value;
    if (isPriceReveal) {
      return {
        description: saleStartEndText,
        discountValue: futureDiscountPercent,
        price: futureBestPrice > 0 ? futureBestPrice : priceValue,
        title: bbsPriceStaticText,
        luxeTitle: luxebbsPriceStaticText,
        userEduText,
        luxeUserEduText,
        futureBestPrice,
        priceComingSoonTxt,
        luxePriceComingSoonTxt,
        priceImgSrc,
        luxePriceImgSrc,
        bannerImgSrc,
        luxeBannerImgSrc,
        saleStartTime
      };
    }
    return null;
  }
);

const getPriceRevealV2WidgetData = createSelector(
  getIsPriceRevealPDP,
  getPriceRevealMobileData,
  (isPriceReveal, productRevealData) => {
    const { hashtag, priceImgSrc, luxeHashtag, luxePriceImgSrc } = productRevealData || {};
    return isPriceReveal ? { hashtag, priceImgSrc, luxeHashtag, luxePriceImgSrc } : '';
  }
);

const getIsPriceRevealRecentlyViewed = createSelector(
  getProduct,
  getPriceRevealV2FeatureFlag,
  getPriceRevealFlagPDP,
  getPriceRevealMobileData,
  getSis,
  getPriceRevealLUXEFlag,
  (
    product,
    priceRevealV2FeatureFlag,
    priceRevealFlagPDP,
    productRevealData,
    isStore,
    isLuxeEnabled
  ) => {
    const { priceRevealMetaInfo } = product?.recentlyViewed || {};
    const { saleStartTime, saleEndTime } = priceRevealMetaInfo || {};
    if (isStore && isStore === 'luxe') {
      return (
        isLuxeEnabled &&
        priceRevealV2FeatureFlag &&
        priceRevealFlagPDP &&
        saleStartTime &&
        saleEndTime
      );
    }
    if (priceRevealV2FeatureFlag && priceRevealFlagPDP && saleStartTime && saleEndTime) {
      return true;
    }
    return false;
  }
);

const getIsPriceRevealSimilarStyle = createSelector(
  getProduct,
  getPriceRevealV2FeatureFlag,
  getPriceRevealFlagPDP,
  getSis,
  getPriceRevealLUXEFlag,
  ({ similarData = {} }, priceRevealV2FeatureFlag, priceRevealFlagPDP, isStore, isLuxeEnabled) => {
    const { priceRevealMetaInfo } = similarData;
    const { saleStartTime, saleEndTime } = priceRevealMetaInfo || {};
    if (isStore && isStore === 'luxe') {
      return (
        isLuxeEnabled &&
        priceRevealV2FeatureFlag &&
        priceRevealFlagPDP &&
        saleStartTime &&
        saleEndTime
      );
    }
    if (priceRevealV2FeatureFlag && priceRevealFlagPDP && saleStartTime && saleEndTime) {
      return true;
    }
    return false;
  }
);
const getIsPriceRevealRecentlyViewedDuringSale = createSelector(
  getIsPriceRevealRecentlyViewed,
  getPriceRevealSaleDuration,
  (isPriceReveal, saleDuration) => {
    if (isPriceReveal && saleDuration === DURING_SALE) {
      return true;
    }
    return false;
  }
);

const getIsPriceRevealSimilarStyleDuringSale = createSelector(
  getIsPriceRevealSimilarStyle,
  getPriceRevealSaleDuration,
  getSis,
  (isPriceReveal, saleDuration, sis) => {
    if (isPriceReveal && saleDuration === DURING_SALE) {
      return true;
    }
    return false;
  }
);
const getImsBatchId = createSelector(
  getSelectedSizeDetails,
  getSizeColorVariant,
  extractSelectionValues,
  ({ selectedSizeCode }, { sizeVariant = [] }) => {
    const selectedSizeVariant = sizeVariant?.filter(({ code }, index) => code === selectedSizeCode);
    const { variantOptionQualifiers } = selectedSizeVariant?.length && selectedSizeVariant[0];
    const imsBatchInfo = variantOptionQualifiers?.filter(
      ({ qualifier }, index) => qualifier === 'imsBatchId'
    );
    return imsBatchInfo?.length ? imsBatchInfo[0]?.value : '';
  }
);
const getCommercialType = createSelector(
  getProductDetails,
  productDetails => productDetails?.commercialType || ''
);

export const getSimilarData: (state: any) => any = state => {
  return state?.similarData || {};
};

export const getRushPDPData: (state: any) => any = state => {
  return state?.rushDeliveryTypeReducer?.pdpDeliveryTypeResponse?.pdpData || [];
};

const getAggregateRating = createSelector(
  getProductDetails,
  productDetails => productDetails?.ratingsResponse?.aggregateRating || ''
);

const getSubRatings = createSelector(getProductDetails, productDetails => {
  const subratings = productDetails?.ratingsResponse?.subRatings;
  const customerWords = [];
  subratings?.map((val, i) => {
    const title = val?.productAttribute;
    const attrRating = val?.attributeRatings;
    const max = attrRating.reduce(function(prev, current) {
      const prevVal = +(prev?.percentageRating.replace('%', '').trim() ?? 0);
      const nextVal = +(current?.percentageRating.replace('%', '').trim() ?? 0);
      return prevVal >= nextVal ? prev : current;
    });
    customerWords.push({ title, largestText: max?.text });
  });
  return { subratings: subratings || '', customerWord: customerWords };
});
const getTextReview = createSelector(getProductDetails, productDetails => {
  const textReview = productDetails?.ratingsResponse?.textReview;
  if (textReview) {
    if (!textReview?.totalReviews) {
      return [];
    }
    if (textReview?.totalReviews <= 2) {
      const response = {
        textReview: textReview?.productReviews,
        totalReviews: textReview.totalReviews
      };
      return response;
    }
    const response = {
      textReview: textReview?.productReviews.slice(0, 2),
      totalReviews: textReview.totalReviews
    };
    return response;
  }
  return [];
});

const getImageReview = createSelector(getProductDetails, productDetails => {
  const imageReview = productDetails?.ratingsResponse?.imageReview;
  return imageReview || {};
});

const getKnowTheProductDetails = createSelector(getProductDetails, productDetails => {
  const knowTheProductDetails = productDetails?.knowTheProductDetails;
  return knowTheProductDetails || {};
});

const getStyleIdeaDetails = createSelector(getProductDetails, productDetails => {
  const styleIdeaDetails = productDetails?.postsResponse?.components;
  return styleIdeaDetails || [];
});

const getBrandInfoDetails = createSelector(getProductDetails, productDetails => {
  console.log('brandInfoDetails', productDetails);
  const brandInfoDetails = productDetails?.brandResponse?.brand;
  return brandInfoDetails || {};
});
const getProductServiceAbility = createSelector(getProductDetails, productDetails => {
  const returnExchangeData = productDetails?.baseOptions?.[0]?.selected; 
  const codAllowed  = productDetails?.fnlProductData?.codAllowed;
  const codDetailsHonoredWithFraud  = productDetails?.fnlProductData?.codDetailsHonoredWithFraud;
  const returnDetailsHonoredWithFraud  = productDetails?.fnlProductData?.returnDetailsHonoredWithFraud;

  return {
    returnExchangeData, codAllowed, codDetailsHonoredWithFraud, returnDetailsHonoredWithFraud
  }
});

// NOTE: FOR PDP TO BAG CONVERSION - RELEVANT PRODUCTS WIDGET
const getProductTags = createSelector(getProductDetails, productDetails => {
  const { tagResponse: { tag: { categoryTags = [] } = {} } = {} } = productDetails;
  return categoryTags;
});


const getCMSAdsData = (
  state: import('../types/rootState').RootState
  ): import('../types/cmsAdsData').CMSAdsBanner => state.cmsAds;
  
const getExternalAdsData = createSelector(getCMSAdsData, adsData => {
  return adsData?.adsBanner;
});

interface FeaturedPlan {
  totalMonthlyAmountPayable: string | number;
  tenure?: string | number;
  type?: 'STANDARD' | 'NO_COST';
  thresholdAmount?: string | number; // firebase
  issuerSlug?: string;
  cashbackApplied?: string;
  instantDiscountApplied?: string;
  bankInterestRate?: string;
}

interface EmiTemplate {
  id: string;
  format: string;
}

const renderEMITemplate = (plan: FeaturedPlan, templateId: string, emiTemplate: EmiTemplate[]): string => {
  const output = Object.keys(emiTemplate).find(template => template === templateId) || '';
  let template = emiTemplate[output] || '';
 
  const tenureValue = Number(plan?.tenure);
  const isValidTenure = Number.isFinite(tenureValue) && tenureValue > 0;
  // Remove tenure block completely if tenure is missing
  if (!isValidTenure) {
    template = template.replace(/\s*\(\{tenure\}\s*months\)/g, '');
  }
  // Replace placeholders dynamically
  template = template.replace('{totalMonthlyAmountPayable}', `<b>${String(plan?.totalMonthlyAmountPayable)}</b>`);
  template = template.replace('{tenure}', plan?.tenure !== undefined ? String(plan?.tenure) : '');
  template = template.replace('{type}', plan?.type === 'NO_COST' ? 'No Cost' : 'Standard');
  template = template.replace('{thresholdAmount}', plan?.thresholdAmount !== undefined ? `${plan?.thresholdAmount}` : '');
  template = template.replace('{issuerSlug}', plan?.issuerSlug);
  template = template.replace('{cashbackApplied}', plan?.cashbackApplied);
  template = template.replace('{instantDiscountApplied}', plan?.instantDiscountApplied);
  template = template.replace('{bankInterestRate}', plan?.bankInterestRate);

  // Clean extra spaces or dangling punctuation if optional values are missing
  template = template.replace(/\s{2,}/g, ' ').replace(/ - $/, '').replace(/\. $/, '');

  return template;
}

const getEmiDetails = createSelector(
  getProductDetails,
  getSelectedVariantOptions,
  getSelectedSlaOption,
  getIsSlaFetching,
  (productDetails, selectedVariant, selectedSlaOption, isSlaFetching) => {
    // Size + pincode → scopedView emi; keep prior / product-level while fetching.
    if (selectedSlaOption) {
      const slaEmi = selectedSlaOption.emi;
      return slaEmi && typeof slaEmi === 'object' ? slaEmi : {};
    }
    if (isSlaFetching) {
      return productDetails?.emi || {};
    }
    // Size only → selected variant emi; else product-level emi.
    const variantEmi = selectedVariant?.emi;
    return variantEmi && Object.keys(variantEmi).length ? variantEmi : productDetails?.emi || {};
  }
);

/**
 * Freebies (gift-with-product) for the PDP strip / modal.
 *
 * scopedView ships `giftPromotion` per SLA option, so gifts follow the selected
 * delivery card the same way price / emi / vouchers do. A selected option is
 * authoritative — an option without gifts shows no freebies, it does not fall
 * back to the aggregator. Without a selection (no size/pincode, or scopedView
 * still in flight) the product-level giftPromotion is used.
 *
 * When scopedView FAILS there is no authoritative option: the slice keeps the
 * prior selectedSlaOption so price / EMI stay stable, but its giftPromotion is
 * stale for the current size, so freebies fall back to the aggregator
 * (pdp product-level giftPromotion) instead. Same path on msite and desktop —
 * both mapStateToProps read this selector.
 */
const getFreebiesDetails = createSelector(
  getProductDetails,
  getSelectedSlaOption,
  getSlaFetchFailed,
  (productDetails, selectedSlaOption, slaFetchFailed) => {
    const giftPromotion =
      selectedSlaOption && !slaFetchFailed
        ? selectedSlaOption.giftPromotion
        : productDetails?.giftPromotion;
    const gifts = giftPromotion?.gifts || [];
    return {
      gifts,
      totalGiftCount: giftPromotion?.totalGiftCount || gifts.length || 0
    };
  }
);

const getIsNonReturnable = state => {
  const pdpisReturnable =
    getProductServiceAbility(state)?.returnExchangeData?.isReturnable ?? 'false';
  const isCheckServiceabilityCalledForRex = getIsCheckServiceabilityCalledForRex(state);
  const serviceabilityIsReturnable = getServiceabilityDetailsDetails(state)?.returnWindow?.actionContent?.isReturnable;
  return isCheckServiceabilityCalledForRex
    ? !(serviceabilityIsReturnable && pdpisReturnable === 'true')
    : pdpisReturnable === 'false';
};

const getProductFnlReturnWindow = createSelector(
  getProductDetails,
  productDetails => productDetails?.fnlProductData?.returnWindow ?? 0
);

export {
  getSis,
  getSegmentId,
  getExperimentId,
  getbrandName,
  getProductName,
  getProductUrl,
  getPriceDetails,
  getProductCode,
  getSimilarStylesDetails,
  getRecentlyViewsDetails,
  getRecentlyViewedProducts,
  getShopTheLook,
  getShopTheCapsule,
  getMoreLinks,
  getGstText,
  getOffersCount,
  getOffersDetails,
  getProductImages,
  getProductZoomImages,
  getMandatoryInfo,
  getB2cNoLocationSellerName,
  getFeatureData,
  getReturnDetails,
  getSizeData,
  getSelectedSizeDetails,
  getAddToCartProps,
  getEddDetails,
  getServiceabilityDetailsDetails,
  getSwatchColors,
  getSizeRecommendFetch,
  isSizeChartAvailable,
  getBrandSizes,
  getInitialDodData,
  getDodSelectedSize,
  getCatDescription,
  getAboutBrandImgUrl,
  getCategoryUrl,
  getExclusiveTill,
  getProductTag,
  getPDPTopLeftProductTag,
  getPDPBelowPriceProductTag,
  getProductTagFromTagEngine,
  getWishlistedCountFromTagEngine,
  getProductPriceLabelFromTagEngine,
  getIsOutOfStock,
  getProductColor,
  getProductCategory,
  getProductVertical,
  getProductSubCategory,
  getEDDFetchingFlag,
  getRecommendSize,
  getSizeRevampFlag,
  getTrustMarkerImages,
  getTrustMarkerPdp,
  getBrickName,
  getErrorPriceRefresh,
  getPricebreakUp,
  getProductMarketPdp,
  getProductCatalog,
  getIsPriceRevealPDP,
  getPriceRevealData,
  getPriceRevealV2WidgetData,
  getPriceRevealSaleDuration,
  getImsBatchId,
  getIsPriceRevealRecentlyViewed,
  getIsPriceRevealSimilarStyle,
  getIsPriceRevealRecentlyViewedDuringSale,
  getIsPriceRevealSimilarStyleDuringSale,
  getIsShoppingAssistantPDP,
  getShoppingAssistantDetails,
  getBankCoupons,
  getBankCouponsFromPDPAggregator,
  youMayAlsoLike,
  getBrickCode,
  getJioAddData,
  getSnekaerHoodData,
  getProductData,
  getAggregateRating,
  getSubRatings,
  getTextReview,
  getImageReview,
  getComprehensiveProductData,
  getSimilarStylesDetailsWithLimit,
  getCrossSellDetails,
  getSimilarStylesDetailsExitIntent,
  getKnowTheProductDetails,
  getStyleIdeaDetails,
  getBrandInfoDetails,
  getThumbnailImage,
  getCommercialType,
  getSpotlightData,
  getProductStock,
  getVariantOptions,
  getProductServiceAbility,
  getServiceabilityFetchingFlag,
  getIsCheckServiceabilityCalled,
  getIsCheckDeliveryStatusCalled,
  getIsCheckServiceabilityCalledForRex,
  getErrorInServiceabilityFetching,
  getProductTags,
  getBrickCategory,
  getPreviousSearchSource,
  getSectionOne,
  getSectionTwo,
  getSectionThree,
  getExternalAdsData,
  getPageCategory,
  renderEMITemplate,
  getEmiDetails,
  getFreebiesDetails,
  getIsComingSoonProduct,
  getPDPTrendsVibes,
  getIsNonReturnable,
  getProductFnlReturnWindow
};
